import Vue from 'vue';
import type { Filters } from '@mimir/common/filters/Filters';
import { ChoiceFilter, createCustomFilterBasedOnField, createFilterKey } from '@mimir/common/filters/Filters';
import { getFiltersFromSearchString } from '@mimir/common/filters/filter-util';
import type { FilterToken, SearchToken, TextRange } from '@mimir/common/filters/searchTokens';
import { getFilterTokens, operatorBetween, removeRanges, tokenizeSearchString } from '@mimir/common/filters/searchTokens';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { MdfFieldEntity } from '@mimir/common/mdf/fieldDefinition';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { StaticFileKeywords, StaticFilterKeywords } from '@mimir/common/dto/searchModels';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type { DateHelper } from '@mimir/common/util/DateUtil';
import type { Logger } from '@mimir/common/types/logger';

/** The inputs the search box hands in to turn its raw text into filter chips: the typed
 *  searchString, the metadata schemas (mdfs) to match field names against, the reactive Filters
 *  state to fill, and the lookups a field needs to parse its value (users, option lists, dates). */
export interface AddCustomFiltersContext {
  searchString: string;
  mdfs: MetaDataSchema[];
  filters: Filters;
  users: UserInfoDto[];
  optionLists: OptionListDto[];
  dateHelper: DateHelper;
  logger: Logger;
}

/** Built-in filter keys (formId, type, the file keywords) that the search box handles on its
 *  own, so the metadata-field pass below skips them. */
const STATIC_FILTER_KEYS: ReadonlySet<string> = new Set<string>([
  ...Object.values(StaticFilterKeywords),
  ...Object.values(StaticFileKeywords),
]);

/**
 * Reads the metadata-field filters out of `searchString`, adds each one as a chip on
 * `context.filters` (mutated in place, since that is the reactive state the search box owns),
 * and returns the leftover free text with the consumed terms removed. So 'sunset person:obama'
 * adds a person chip and returns 'sunset'. The caller commits the returned string.
 */
export function addCustomFiltersToSearchString(context: AddCustomFiltersContext): string {
  const { searchString, mdfs, filters } = context;
  if (mdfs.length === 0) return searchString;

  const terms = getFiltersFromSearchString(searchString) ?? [];
  const formIdTerm = terms.find(term => term.startsWith('formId:'));

  const mdf = resolveSelectedMdf(formIdTerm, mdfs, filters);
  if (!mdf) return searchString;

  const withoutFormId = formIdTerm ? searchString.replace(formIdTerm, '') : searchString;
  return applyFieldFilters(terms, mdf, withoutFormId, context);
}

/** Resolves the form from an explicit `formId:` term, or the default form when there is none. */
function resolveSelectedMdf(formIdTerm: string | undefined, mdfs: MetaDataSchema[], filters: Filters): MetaDataSchema | null {
  return selectMdf(formIdTerm?.split(':')[1] || DEFAULT_MDF_ID, mdfs, filters);
}

/** Finds the form by id or label, e.g. 'interview' or its label, and for a non-default form
 *  reflects the selection on the formId filter chip. Returns null when nothing matches. */
function selectMdf(formIdOrLabel: string, mdfs: MetaDataSchema[], filters: Filters): MetaDataSchema | null {
  const mdf = mdfs.find(schema => schema.id === formIdOrLabel || schema.label === formIdOrLabel);
  if (!mdf) return null;

  if (mdf.id !== DEFAULT_MDF_ID) {
    const formIdFilter = filters.defaultFilters['filter:formId'];
    formIdFilter.alias = mdf.label;
    formIdFilter.parseValue(mdf.label, formIdFilter.hasSticky);
  }
  return mdf;
}

/** Turns each `field:value` term into a custom filter chip and strips the consumed terms from
 *  the text. Skips built-in keys, unknown fields, a value already on that chip, and values that
 *  parse to nothing. So 'person:obama' becomes a person chip and leaves the rest as free text. */
function applyFieldFilters(terms: string[], mdf: MetaDataSchema, searchString: string, context: AddCustomFiltersContext): string {
  const fieldsById = buildFieldLookup(mdf);
  let residual = searchString;

  for (const term of terms) {
    const [fieldId, value] = term.split(':');
    if (STATIC_FILTER_KEYS.has(fieldId)) continue;

    const field = fieldsById.get(fieldId);
    if (!field) continue;

    if (addCustomFilterForField(field, value, mdf, context)) {
      residual = residual.replace(term, '');
    }
  }
  return residual;
}

/** Creates the custom filter for `field` with `value` and stores it on `context.filters`,
 *  unless an equal value is already there or the value is empty. Returns whether it stored one,
 *  which is the signal to drop the matching term from the leftover text. */
function addCustomFilterForField(field: MdfFieldEntity, value: string, mdf: MetaDataSchema, context: AddCustomFiltersContext): boolean {
  const key = createFilterKey(field);
  const existing = context.filters.customFilters[key];
  if (existing && existing.getValue() === value) return false;

  const filter = createCustomFilterBasedOnField(context.logger, field, mdf.id, context.dateHelper, undefined, context.users, context.optionLists);
  filter.parseValue(value);
  if (!filter.hasValue) return false;

  Vue.set(context.filters.customFilters, key, filter);
  return true;
}

/** Indexes a form's fields by both ids a term might use, the fieldId and the entity id, so a
 *  term written either way resolves to the same field. */
function buildFieldLookup(mdf: MetaDataSchema): Map<string, MdfFieldEntity> {
  const fieldsById = new Map<string, MdfFieldEntity>();
  for (const field of mdf.fields ?? []) {
    fieldsById.set(field.fieldId, field);
    fieldsById.set(field.id, field);
  }
  return fieldsById;
}

/** A field together with every token that targeted it in one parse. */
interface FieldTokenGroup {
  field: MdfFieldEntity;
  key: string;
  tokens: FilterToken[];
}

/** The new-parser twin of addCustomFiltersToSearchString. A field typed more than once keeps every
 *  value. Consumed filter terms, and any operator left stranded next to one, are cut from the
 *  returned free text. */
export function applyCustomFilters(context: AddCustomFiltersContext): string {
  const { searchString, mdfs, filters } = context;
  if (mdfs.length === 0) return searchString;

  const tokens = tokenizeSearchString(searchString);
  const filterTokens = getFilterTokens(tokens);
  const formIdToken = filterTokens.find(token => token.field === 'formId');

  const mdf = selectMdf(formIdToken?.value || DEFAULT_MDF_ID, mdfs, filters);
  if (!mdf) return searchString;

  const consumed = new Set<SearchToken>();
  if (formIdToken) {
    consumed.add(formIdToken);
  }

  for (const group of groupTokensByField(filterTokens, buildFieldLookup(mdf))) {
    if (storeFieldFilter(group, mdf, context, tokens)) {
      for (const token of group.tokens) {
        consumed.add(token);
      }
    }
  }

  return removeRanges(searchString, consumedRanges(tokens, consumed));
}

/** The positions to cut: every consumed filter token, plus any operator next to one. An
 *  operator that lost its neighbouring filter would otherwise dangle in the leftover. */
function consumedRanges(tokens: SearchToken[], consumed: Set<SearchToken>): TextRange[] {
  const ranges: TextRange[] = [];
  for (const [index, token] of tokens.entries()) {
    if (consumed.has(token)) {
      ranges.push(token.position);
    } else if (token.type === 'operator' && touchesConsumedFilter(tokens, index, consumed)) {
      ranges.push(token.position);
    }
  }
  return ranges;
}

/** Whether the token just before or just after this one is a consumed filter. */
function touchesConsumedFilter(tokens: SearchToken[], index: number, consumed: Set<SearchToken>): boolean {
  const before = tokens[index - 1];
  const after = tokens[index + 1];
  return (before !== undefined && consumed.has(before)) || (after !== undefined && consumed.has(after));
}

/** Buckets the tokens by resolved field, so repeats of one field share a group and their values
 *  stack. Static keys and unknown fields are dropped. */
function groupTokensByField(filterTokens: FilterToken[], fieldsById: Map<string, MdfFieldEntity>): FieldTokenGroup[] {
  const groupsByKey = new Map<string, FieldTokenGroup>();
  for (const token of filterTokens) {
    if (STATIC_FILTER_KEYS.has(token.field)) continue;

    const field = fieldsById.get(token.field);
    if (!field) continue;

    const key = createFilterKey(field);
    const group = groupsByKey.get(key);
    if (group) {
      group.tokens.push(token);
    } else {
      groupsByKey.set(key, { field, key, tokens: [token] });
    }
  }
  return [...groupsByKey.values()];
}

/** A fresh filter per call, so a new search resets the field instead of stacking onto the last one.
 *  Whether repeated values stack or the last wins is the filter type's own behaviour: text and
 *  choice stack, user and checkbox keep the last. */
function storeFieldFilter(group: FieldTokenGroup, mdf: MetaDataSchema, context: AddCustomFiltersContext, tokens: SearchToken[]): boolean {
  const filter = createCustomFilterBasedOnField(context.logger, group.field, mdf.id, context.dateHelper, undefined, context.users, context.optionLists);
  for (const token of group.tokens) {
    filter.parseValue(token.value);
  }
  if (!filter.hasValue) return false;

  if (filter instanceof ChoiceFilter) {
    filter.multiValueOperator = typedOperatorForGroup(tokens, group);
  }
  Vue.set(context.filters.customFilters, group.key, filter);
  return true;
}

/** The operator the user typed between the group's first two values, or undefined when the field
 *  was typed once or with no operator between its values. */
function typedOperatorForGroup(tokens: SearchToken[], group: FieldTokenGroup): 'AND' | 'OR' | undefined {
  const [first, second] = group.tokens;
  if (first === undefined || second === undefined) {
    return undefined;
  }
  return operatorBetween(tokens, tokens.indexOf(first), tokens.indexOf(second)) ?? undefined;
}

/** URL half of the operator round-trip, for the new-parsing path: reads the operator typed
 *  between repeated values of one choice field in the restored search string and sets it on the
 *  already rebuilt chip, so a reloaded or shared search keeps its OR/AND. */
export function applyMultiValueOperators(searchString: string, mdfs: MetaDataSchema[], filters: Filters): void {
  if (mdfs.length === 0) return;

  const tokens = tokenizeSearchString(searchString);
  const filterTokens = getFilterTokens(tokens);
  const formIdToken = filterTokens.find(token => token.field === 'formId');

  const mdf = selectMdf(formIdToken?.value || DEFAULT_MDF_ID, mdfs, filters);
  if (!mdf) return;

  for (const group of groupTokensByField(filterTokens, buildFieldLookup(mdf))) {
    const filter = filters.customFilters[group.key];
    if (filter instanceof ChoiceFilter) {
      filter.multiValueOperator = typedOperatorForGroup(tokens, group);
    }
  }
}
